import { buildShareUrl } from '../../utils/share';
import { getShareOrigin } from '../../services/api';
import type {
  GamificationShareCard as GamificationShareCardDto,
  GamificationShareType,
} from '../../types/auth';

export type { GamificationShareType } from '../../types/auth';

export const SHARE_TAB = 'lightPath';

export type CanonicalShareMedallionKey =
  | 'flame'
  | 'sprout'
  | 'growth'
  | 'soul_light'
  | 'shield_star'
  | 'progress'
  | 'calendar_plus'
  | 'live_discover';

export type GamificationShareVisualIcon =
  | { kind: 'single'; key: CanonicalShareMedallionKey }
  | { kind: 'triplet'; levelKey: CanonicalShareMedallionKey };

export interface GamificationShareViewModel {
  type: GamificationShareType;
  id: string;
  category: string;
  title: string;
  value: string;
  description: string;
  displayName: string;
  avatarUrl: string | null;
  icon: GamificationShareVisualIcon;
}

const LEVEL_ICON: Record<string, CanonicalShareMedallionKey> = {
  seeker: 'sprout',
  keeper: 'growth',
  master: 'soul_light',
  grand_master: 'shield_star',
};

const LEVEL_DESCRIPTION: Record<string, string> = {
  seeker: 'Первый шаг на Пути Света',
  keeper: 'Сохраняю Пламя и развиваю привычку',
  master: 'Регулярная практика и высокий уровень активности',
  grand_master: 'Максимальная ступень развития на платформе',
};

const ACHIEVEMENT_META: Record<string, { icon: CanonicalShareMedallionKey; description: string }> = {
  first_spark: { icon: 'flame', description: 'Пламя Любви горит 3 дня подряд' },
  week_of_light: { icon: 'soul_light', description: 'Пламя Любви горит 7 дней подряд' },
  habit_sprout: { icon: 'sprout', description: 'Пламя Любви горит 21 день подряд' },
  forty_days: { icon: 'calendar_plus', description: 'Пламя Любви горит 40 дней подряд' },
  sacred_mala: { icon: 'progress', description: 'Пламя Любви горит 108 дней подряд' },
  wheel_of_year: { icon: 'shield_star', description: 'Пламя Любви горит 365 дней подряд' },
  first_practice: { icon: 'sprout', description: 'Первая практика завершена' },
  first_live: { icon: 'live_discover', description: 'Первая Live-сессия завершена' },
};

const MILESTONE_ICON: Record<string, CanonicalShareMedallionKey> = {
  '3': 'flame',
  '7': 'soul_light',
  '21': 'sprout',
  '40': 'calendar_plus',
  '108': 'progress',
  '365': 'shield_star',
};

export class GamificationShareContractError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GamificationShareContractError';
  }
}

const contractError = (message: string): never => {
  const error = new GamificationShareContractError(message);
  console.error('[CORE-709] Gamification share contract mismatch:', message);
  throw error;
};

const dayWord = (value: number): 'день' | 'дня' | 'дней' => {
  const mod100 = Math.abs(value) % 100;
  const mod10 = mod100 % 10;
  if (mod100 >= 11 && mod100 <= 14) return 'дней';
  if (mod10 === 1) return 'день';
  if (mod10 >= 2 && mod10 <= 4) return 'дня';
  return 'дней';
};

const finiteNonNegative = (value: unknown): value is number => (
  typeof value === 'number' && Number.isFinite(value) && value >= 0
);

/**
 * The link a share actually carries.
 *
 * CORE-966 — with a token this is `/s/{token}`, the public share URL whose
 * server-rendered Open Graph tags unfold into the very card below. Without one
 * it falls back to the old profile link: an older backend (or a card that could
 * not be loaded) must still produce a working link, just a plainer preview.
 */
export function buildGamificationShareUrl(
  userId?: string | number | null,
  shareToken?: string | null,
): string {
  if (shareToken) return buildShareUrl('gamification', shareToken);
  if (userId == null || userId === '') return getShareOrigin();
  return buildShareUrl('user', userId, { query: { tab: SHARE_TAB } });
}

/**
 * Turns the network DTO into the one approved visual contract. Unknown stable
 * ids deliberately fail instead of borrowing a visually similar medallion.
 */
export function buildGamificationShareViewModel(input: {
  requestedType: GamificationShareType;
  card: GamificationShareCardDto;
  progressCard?: GamificationShareCardDto | null;
  fallbackDisplayName?: string | null;
}): GamificationShareViewModel {
  const { requestedType, card, progressCard, fallbackDisplayName } = input;
  if (card.type !== requestedType) {
    return contractError(`expected type ${requestedType}, received ${String(card.type)}`);
  }
  if (!card.id || !card.title) return contractError('card id/title is empty');

  const displayName = card.displayName?.trim() || fallbackDisplayName?.trim() || 'Пользователь Agapella';
  const base = {
    type: requestedType,
    id: card.id,
    displayName,
    avatarUrl: card.avatarUrl ?? null,
  };

  if (requestedType === 'level') {
    const icon = LEVEL_ICON[card.id];
    if (!icon) return contractError(`unknown level code: ${card.id}`);
    const progress = progressCard?.type === 'progress' ? progressCard.progress : null;
    const currentXp = progress?.levelCode === card.id && finiteNonNegative(progress.xp) ? progress.xp : null;
    return {
      ...base,
      category: 'Уровень духовного пути',
      title: card.title,
      value: currentXp == null ? 'Уровень получен' : `${currentXp.toLocaleString('ru-RU')} XP`,
      description: LEVEL_DESCRIPTION[card.id],
      icon: { kind: 'single', key: icon },
    };
  }

  if (requestedType === 'achievement') {
    const meta = ACHIEVEMENT_META[card.id];
    if (!meta) return contractError(`unknown achievement key: ${card.id}`);
    return {
      ...base,
      category: 'Получено достижение',
      title: card.title,
      value: 'Бейдж получен',
      description: meta.description,
      icon: { kind: 'single', key: meta.icon },
    };
  }

  if (requestedType === 'streak') {
    if (!finiteNonNegative(card.value) || card.value <= 0) return contractError('streak value must be positive');
    return {
      ...base,
      category: 'Пламя Любви',
      title: card.title,
      value: 'Текущий стрик',
      description: 'Практикую каждый день',
      icon: { kind: 'single', key: 'flame' },
    };
  }

  if (requestedType === 'milestone') {
    const icon = MILESTONE_ICON[card.id];
    if (!icon || !finiteNonNegative(card.value) || String(card.value) !== card.id) {
      return contractError(`invalid milestone id/value pair: ${card.id}/${String(card.value)}`);
    }
    return {
      ...base,
      category: 'Новая веха',
      title: card.title,
      value: `${card.value} ${dayWord(card.value)} подряд`,
      description: 'Новая веха на Пути Света',
      icon: { kind: 'single', key: icon },
    };
  }

  if (requestedType === 'record') {
    if (!finiteNonNegative(card.value) || card.value <= 0) return contractError('record value must be positive');
    return {
      ...base,
      category: 'Личный рекорд',
      title: `${card.value} ${dayWord(card.value)}`,
      value: 'Максимальный стрик',
      description: 'Новый личный рекорд',
      icon: { kind: 'single', key: 'flame' },
    };
  }

  const progress = card.progress;
  if (
    card.id !== 'current'
    || !progress
    || !finiteNonNegative(progress.currentStreak)
    || !finiteNonNegative(progress.xp)
    || !LEVEL_ICON[progress.levelCode]
    || !progress.levelName?.trim()
  ) {
    return contractError('invalid progress/current payload');
  }
  return {
    ...base,
    category: card.subtitle?.trim() || 'Путь Света',
    title: progress.levelName,
    value: `${progress.xp.toLocaleString('ru-RU')} XP`,
    description: `Пламя Любви · ${progress.currentStreak} ${dayWord(progress.currentStreak)}`,
    icon: { kind: 'triplet', levelKey: LEVEL_ICON[progress.levelCode] },
  };
}

/**
 * Whether a link is a CORE-966 token link — the only shape whose external
 * preview is the personal card. Read from the URL rather than passed alongside
 * it so the modal's promise cannot disagree with the link it is about to send.
 */
export function isGamificationShareUrl(url: string): boolean {
  const path = url.split('?')[0];
  return /\/s\/[^/]+$/.test(path);
}

export interface ShareCopyInput {
  type: GamificationShareType;
  card?: GamificationShareCardDto | null;
  progressCard?: GamificationShareCardDto | null;
  fallbackMessage?: string;
  displayName?: string | null;
  url: string;
}

export interface ShareCopy {
  message: string;
  title: string;
  subject: string;
  preview: {
    title: string;
    category: string;
    name: string | null;
    caption: string;
    viewModel: GamificationShareViewModel | null;
  };
}

const TYPE_LABEL: Record<GamificationShareType, string> = {
  level: 'Уровень духовного пути',
  achievement: 'Достижение',
  streak: 'Пламя Любви',
  milestone: 'Веха пути',
  record: 'Личный рекорд',
  progress: 'Путь Света',
};

export function buildShareCopy({ type, card, progressCard, fallbackMessage, displayName, url }: ShareCopyInput): ShareCopy {
  const title = card?.title ?? 'Путь Света';
  const category = card?.subtitle ?? TYPE_LABEL[type];
  const viewModel = card
    ? buildGamificationShareViewModel({ requestedType: type, card, progressCard, fallbackDisplayName: displayName })
    : null;
  return {
    message: `${card?.message ?? fallbackMessage ?? 'Мой Путь Света в Agapella'} ${url}`.trim(),
    title,
    subject: card?.subtitle ? `${card.subtitle}: ${card.title}` : title,
    preview: {
      title,
      category,
      name: card?.displayName ?? displayName ?? null,
      caption: 'Agapella · Путь Света',
      viewModel,
    },
  };
}
